import React, { useState, useEffect } from "react";
import { 
  FiUsers, 
  FiSearch, 
  FiFilter, 
  FiBookOpen, 
  FiUserCheck, 
  FiMoreVertical, 
  FiRefreshCw,
  FiLayers
} from "react-icons/fi";
import api from "../../connector/api";

export default function ClassroomTab() {
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState("all");

  // Fetch classroom data on component mount
  useEffect(() => {
    fetchClassrooms();
  }, []);

  const fetchClassrooms = async () => {
    setLoading(true);
    try {
      const response = await api.get("report/classrooms/list");
      // Handle paginated (response.data.results) or direct list (response.data) responses
      const data = Array.isArray(response.data) 
        ? response.data 
        : response.data?.results || [];
      setClassrooms(data);
    } catch (error) {
      console.error("Failed to fetch classroom data:", error);
      // Fallback mock data matching serializer response keys
      setClassrooms([
        {
          id: 1,
          name: "Section Alpha",
          section_name: "Section Alpha",
          room_code: "Room 101",
          school_year_name: "2025/2026",
          teacher_name: "Doe, John",
          student_count: 28,
          subject_count: 5,
        },
        {
          id: 2,
          name: "Section Beta",
          section_name: "Section Beta",
          room_code: "Room 102",
          school_year_name: "2025/2026",
          teacher_name: "Smith, Jane",
          student_count: 30,
          subject_count: 6,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Extract unique school years dynamically for filtering
  const availableYears = Array.from(
    new Set(classrooms.map((item) => item.school_year_name).filter(Boolean))
  );

  // Filter classrooms based on user search query and school year
  const filteredClassrooms = classrooms.filter((item) => {
    const classroomName = item.name || item.section_name || "";
    const teacherName = item.teacher_name || "";
    const roomCode = item.room_code || "";

    const matchesSearch = 
      classroomName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      teacherName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      roomCode.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesYear = 
      selectedYear === "all" || item.school_year_name === selectedYear;

    return matchesSearch && matchesYear;
  });

  return (
    <div className="space-y-6">
      {/* Controls & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Search Input */}
          <div className="relative w-full sm:w-64">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <input
              type="text"
              placeholder="Search section, teacher, or room..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-white border border-zinc-200/80 rounded-lg text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 transition-all"
            />
          </div>

          {/* School Year Dynamic Filter Dropdown */}
          <div className="relative">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="appearance-none pl-8 pr-8 py-1.5 bg-white border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-400 transition-all cursor-pointer"
            >
              <option value="all">All School Years</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <FiFilter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400 pointer-events-none" />
          </div>
        </div>

        {/* Action Controls */}
        <button
          onClick={fetchClassrooms}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200/80 hover:bg-zinc-100/80 text-zinc-700 text-xs font-medium rounded-lg transition-colors shadow-2xs self-end sm:self-auto"
        >
          <FiRefreshCw className={`w-3.5 h-3.5 text-zinc-500 ${loading ? "animate-spin" : ""}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Classroom Grid Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-400 space-y-2">
          <FiRefreshCw className="w-6 h-6 animate-spin text-zinc-300" />
          <p className="text-xs font-medium">Loading classroom records...</p>
        </div>
      ) : filteredClassrooms.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClassrooms.map((cls) => (
            <div
              key={cls.id}
              className="group bg-white border border-zinc-200/80 rounded-xl p-4 shadow-2xs hover:border-zinc-300 hover:shadow-xs transition-all flex flex-col justify-between"
            >
              <div>
                {/* Card Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-700 group-hover:bg-zinc-900 group-hover:text-white transition-colors">
                      <FiBookOpen className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-zinc-900 tracking-tight">
                        {cls.name || cls.section_name || `Classroom #${cls.id}`}
                      </h3>
                      {cls.school_year_name && (
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-600 border border-zinc-200/50 mt-0.5">
                          {cls.school_year_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <button className="text-zinc-400 hover:text-zinc-600 p-1 rounded-md transition-colors">
                    <FiMoreVertical className="w-4 h-4" />
                  </button>
                </div>

                {/* Card Details */}
                <div className="space-y-2 my-4 pt-2 border-t border-zinc-100 text-xs">
                  <div className="flex items-center justify-between text-zinc-600">
                    <span className="flex items-center gap-1.5 text-zinc-400">
                      <FiUserCheck className="w-3.5 h-3.5" /> Homeroom Teacher:
                    </span>
                    <span className="font-semibold text-zinc-800">
                      {cls.teacher_name || "Unassigned"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-zinc-600">
                    <span className="flex items-center gap-1.5 text-zinc-400">
                      <FiUsers className="w-3.5 h-3.5" /> Enrolled Students:
                    </span>
                    <span className="font-semibold text-zinc-800">
                      {cls.student_count ?? 0} Students
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-zinc-600">
                    <span className="flex items-center gap-1.5 text-zinc-400">
                      <FiLayers className="w-3.5 h-3.5" /> Total Subjects:
                    </span>
                    <span className="font-semibold text-zinc-800">
                      {cls.subject_count ?? 0} Subjects
                    </span>
                  </div>
                </div>
              </div>

              {/* Card Footer Action */}
              <div className="pt-3 border-t border-zinc-100 flex items-center justify-between">
                <span className="text-[11px] font-medium text-zinc-400">
                  {cls.room_code || "No Room"}
                </span>
                <button className="text-xs font-semibold text-zinc-700 hover:text-zinc-900 transition-colors">
                  View Details &rarr;
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-zinc-200 rounded-xl text-zinc-400 space-y-2">
          <FiUsers className="w-8 h-8 text-zinc-300" />
          <p className="text-xs font-semibold text-zinc-600">No Classrooms Found</p>
          <p className="text-[11px] text-zinc-400">
            Try adjusting your search query or filter settings.
          </p>
        </div>
      )}
    </div>
  );
}